import React, { useEffect, useRef, useState } from "react";
import { useLocation } from "react-router-dom";

const KEY = "cs.audio.muted";
const HIDE_ON = ["/", "/splash"]; // splash owns its own intro audio

// User preference — not sensitive; localStorage is intentional and safe here.
const loadMuted = () => {
  // Wrapped in try/catch to survive private-mode / disabled-storage browsers.
  try {
    return localStorage.getItem(KEY) === "1";
  } catch (err) {
    if (process.env.NODE_ENV !== "production") console.debug("AudioLoop: localStorage unavailable", err);
    return false;
  }
};

/**
 * Global background music loop. Plays on any route except / and /splash.
 * Mute state is controlled by the SettingsMenu component (via localStorage + custom event).
 */
export const AudioLoop = () => {
  const loc = useLocation();
  const audioRef = useRef(null);
  const [muted, setMuted] = useState(() => loadMuted());
  const hidden = HIDE_ON.includes(loc.pathname);

  // React to SettingsMenu toggle
  useEffect(() => {
    const onToggle = (e) => setMuted(!!e.detail?.muted);
    const onStorage = (e) => { if (e.key === KEY) setMuted(e.newValue === "1"); };
    window.addEventListener("cs:audio-toggle", onToggle);
    window.addEventListener("storage", onStorage);
    return () => {
      window.removeEventListener("cs:audio-toggle", onToggle);
      window.removeEventListener("storage", onStorage);
    };
  }, []);

  useEffect(() => {
    const a = audioRef.current;
    if (!a) return;
    a.volume = 0.35;
    a.loop = true;
    if (hidden) {
      try { a.pause(); } catch {}
      return;
    }
    if (muted) return;
    const p = a.play();
    if (p && typeof p.then === "function") p.catch(() => {});
  }, [loc.pathname, hidden, muted]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.muted = muted;
  }, [muted]);

  // If autoplay is blocked, resume on the first user interaction anywhere.
  useEffect(() => {
    if (hidden || muted) return;
    const handler = () => {
      const a = audioRef.current;
      if (a && a.paused) a.play().catch(() => {});
    };
    window.addEventListener("pointerdown", handler, { once: true });
    return () => window.removeEventListener("pointerdown", handler);
  }, [hidden, muted, loc.pathname]);

  return (
    <audio
      ref={audioRef}
      src="/brand/loop.mp3"
      preload="auto"
      loop
      playsInline
      data-testid="app-loop-audio"
    />
  );
};
